import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { PLAN_PRICES_VND, SUBSCRIPTION_RENEWAL_LEAD_DAYS, applyPercentOff, perMonthFromAnnualVnd } from '@repo/shared-types';
import type { PromoCodeCheck, PromoCodeReason } from '@repo/shared-types';
import { Button } from '@repo/ui/src/components/ui/button';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import {
  useSubscription,
  useCancelSubscription,
  useCancelSubscriptionInvoice,
  useResumeSubscription,
  useUpgradeSubscription,
} from '../../../hooks/useSubscription';
import { useReconcileStatus } from '../../../hooks/useReconcileStatus';
import { useUpgradeDialogStore } from '../../../stores/upgradeDialogStore';
import { useCan } from '../../../hooks/useCan';
import { PromoCodeRow } from '../../../components/upgrade/PromoCodeRow';

/**
 * `currentPeriodEnd`, `dueAt` and `paidAt` are true UTC instants — NOT the
 * house-frame wall clock `fmtHouseDate` exists for (INVARIANTS §2). So they
 * format through an explicit Asia/Ho_Chi_Minh timezone, the way the
 * scheduledChange line already did. A bare toLocaleDateString prints the
 * viewer's own day, which can disagree with the invoice they are about to pay.
 */
function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
}

/**
 * The day the tick will raise the next renewal invoice: SUBSCRIPTION_RENEWAL_LEAD_DAYS
 * before the period ends. Until now this date was invisible until the nudge
 * banner opened in the final week, even though the data was always here.
 * Subtract on the instant with UTC getters, then format in VN time — mixing the
 * two frames is what made an earlier renewal calculation a day out.
 */
function fmtNextInvoiceDate(periodEndIso: string) {
  const d = new Date(periodEndIso);
  d.setUTCDate(d.getUTCDate() - SUBSCRIPTION_RENEWAL_LEAD_DAYS);
  return fmtDate(d.toISOString());
}

function fmtVnd(n: number, lang: string) {
  const locale = lang.startsWith('vi') ? 'vi-VN' : 'en-US';
  return `${n.toLocaleString(locale)}đ`;
}

type PurchasableCode = 'PLUS' | 'PRO' | 'CENTER';
const PURCHASABLE: PurchasableCode[] = ['PLUS', 'PRO', 'CENTER'];
const TIER_RANK: Record<string, number> = { FREE: 0, PLUS: 1, PRO: 2, CENTER: 3 };

export function SubscriptionTab() {
  const { t, i18n } = useTranslation('billing');
  const { data, isLoading, error } = useSubscription();
  const { data: reconcile } = useReconcileStatus();
  const cancel = useCancelSubscription();
  const resume = useResumeSubscription();
  const cancelInvoice = useCancelSubscriptionInvoice();
  const upgrade = useUpgradeSubscription();
  const canManageBilling = useCan('manage:settings');
  const openUpgrade = useUpgradeDialogStore((s) => s.open);
  const navigate = useNavigate();
  // Stopping renewal only stops the NEXT invoice — it does not end the plan
  // today. Doing that silently on one click left teachers unsure what they had
  // just done, so every direction confirms and spells out the consequence.
  // Cancelling a pending invoice joins them: it is a money action, and the app
  // uses AlertDialog for those everywhere else.
  const [confirming, setConfirming] = useState<'cancel' | 'resume' | 'cancelInvoice' | null>(null);
  const [pickedCycle, setPickedCycle] = useState<'MONTHLY' | 'ANNUAL' | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);
  // THI-200: declared above the early loading/error returns below, same as
  // every other piece of state here — react-hooks/rules-of-hooks forbids a
  // hook called only on some renders.
  const [promo, setPromo] = useState<{ code: string; check: Extract<PromoCodeCheck, { valid: true }> } | null>(
    null,
  );
  // Fix wave (Important #1): a PROMO_* rejection from the upgrade mutation
  // renders inline next to the code row, not in `buyError`'s generic block.
  const [promoError, setPromoError] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 max-w-2xl">
        <div className="border border-surface-200 rounded-lg p-5 bg-surface-0 space-y-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-5 w-20 rounded" />
          </div>
          <Skeleton className="h-3 w-48" />
          <Skeleton className="h-3 w-56" />
        </div>
        <div className="border border-surface-200 rounded-lg p-5 bg-surface-0 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-3 w-full" />
          ))}
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="p-6 text-red-700 bg-red-50 border border-red-200 rounded-md max-w-2xl">
        {(error as Error | null)?.message ?? 'Không tải được thông tin gói. Kiểm tra API server đã chạy chưa.'}
      </div>
    );
  }

  const { plan, usage } = data;
  const code = plan.code;
  const onTrial = data.status === 'TRIALING';
  const onPaidTerm = !onTrial && code !== 'FREE';
  // A running paid term still has unused value, which the upgrade invoice credits.
  const prorates = onPaidTerm && plan.priceVnd > 0;
  // The one state the old copy got wrong: this is not "will cancel", it is
  // "no further invoice will be raised, and the account lands on Free".
  const renewalStopped = onPaidTerm && !data.autoRenew;
  const cycle = pickedCycle ?? (onPaidTerm ? data.billingCycle : 'ANNUAL');

  // Exactly the condition GET /bank-accounts/reconcile-status reports as
  // `status: 'paused'`; derived here rather than string-matched so the two
  // cannot drift. -1 quota (should no longer exist after pricing v2) is
  // unlimited and never exhausted.
  const reconcileExhausted =
    !!reconcile && !reconcile.unlimited && reconcile.quota > 0 && reconcile.used >= reconcile.quota;

  // -1 means unlimited (excluded from the render gate below too), so only a
  // real positive limit can be exhausted — mirrors reconcileExhausted above.
  const aiCreditsExhausted = usage.aiCreditsLimit > 0 && usage.aiCreditsUsed >= usage.aiCreditsLimit;

  // The renewal/trial consequence ("về gói Free") is only true when NOT paying
  // this specific pending invoice is what would drop the account: a renewal
  // invoice, or a trial's first purchase. `extendsTerm` (apps/api's
  // subscription route) carries that fact from the one place that can compute
  // it — the API knows the invoice's periodStart vs. the subscription's
  // currentPeriodEnd, the dashboard only sees the serialized payload.
  // autoRenew is deliberately NOT part of this gate any more (fix round 2): a
  // renewal invoice only ever exists because autoRenew was on when it was
  // raised, and a mid-cycle upgrade invoice must never show the fallback
  // copy regardless of the account's current autoRenew value. A FREE teacher
  // buying up still gets the neutral copy too, via `onPaidTerm` being false.
  const pendingInvoiceHasFreeFallback = Boolean(data.pendingInvoice?.extendsTerm) && (onTrial || onPaidTerm);

  // THE ATTENTION LAW (mock 3f): ochre fires only when a ceiling BLOCKS an
  // action, never when it merely degrades softly. Free's student cap is the one
  // real block — at 10/10 no student can be added — so it warns one seat early.
  // AI credits and auto-reconcile only stop doing things automatically; manual
  // work continues, so they keep attention at exactly 100% (`exhausted`). A 90%
  // rule on every meter would burn the system's earned 10% colour constantly,
  // because AI credits touch 90% routinely and nothing is wrong when they do.
  const studentsCapped = plan.maxStudents > 0;
  const studentsNearLimit = studentsCapped && plan.maxStudents - usage.students <= 1;

  // Current plan is already the card above; repeating it as a purchasable row is
  // noise. A trial is the exception — nothing has been bought yet, so all three
  // are on offer.
  const offered = PURCHASABLE.filter((c) => onTrial || TIER_RANK[c]! > TIER_RANK[code]!);
  // A purchase already scheduled must land before another is bought on top of it
  // (the API rejects the second), so offer none.
  const showChooser = canManageBilling && !data.scheduledChange && offered.length > 0;

  // THI-200: one PromoCodeRow for the whole chooser (see PromoCodeRow) needs a
  // single plan context to check the code against — the cheapest offered
  // tier, since `offered` is ordered PLUS→PRO→CENTER and that is the plan
  // most teachers pick first.
  const contextPlan: PurchasableCode | null = offered[0] ?? null;
  // See UpgradeDialog's matching comment: an accepted code is tied to neither
  // plan nor cycle; PromoCodeRow re-checks it when the cycle toggle moves.
  const activePromo = contextPlan ? promo : null;
  const offer = data.offer;

  // Fix wave (Important #1): shows the code's own line under EVERY plan the
  // winning promotion covers (see UpgradeDialog's matching comment), not just
  // the one plan the code happened to be checked against.
  function offerLineFor(p: PurchasableCode): { pct: number; name: string } | null {
    if (activePromo && activePromo.check.planCodes.includes(p) && activePromo.check.cycles.includes(cycle)) {
      return { pct: activePromo.check.percentOff, name: activePromo.check.name };
    }
    if (offer && offer.planCodes.includes(p) && offer.cycles.includes(cycle)) {
      return { pct: offer.percentOff, name: offer.name };
    }
    return null;
  }

  // Fix wave: the accepted code is sent for WHICHEVER plan is bought — see
  // UpgradeDialog's matching comment.
  //
  // Fix wave 2 (regression fix): sending the code UNCONDITIONALLY (wave 1)
  // 400'd a purchase the campaign alone would have discounted, whenever the
  // code didn't cover the clicked plan/cycle. `check.planCodes`/`.cycles` are
  // now the CODE's OWN coverage (server fix), so the code is sent ONLY when
  // it covers the plan AND cycle actually being bought; otherwise it is
  // omitted, the inline note explains why, and the purchase proceeds (the
  // server still applies an eligible campaign on its own). The PROMO_* catch
  // below stays for genuine server-side rejections of a code that WAS sent.
  const handleUpgrade = async (planCode: PurchasableCode) => {
    setBuyError(null);
    setPromoError(null);
    let promoCode: string | undefined;
    if (activePromo) {
      const coversPlan = activePromo.check.planCodes.includes(planCode);
      const coversCycle = activePromo.check.cycles.includes(cycle);
      if (coversPlan && coversCycle) {
        promoCode = activePromo.code;
      } else {
        setPromoError(t(`offer.reason.${coversPlan ? 'CYCLE' : 'PLAN'}`));
      }
    }
    try {
      const res = await upgrade.mutateAsync({ planCode, billingCycle: cycle, promoCode });
      navigate(`/settings/billing/invoices/${res.subscriptionInvoiceId}`);
    } catch (err) {
      const code = (err as Error & { code?: string }).code;
      if (code?.startsWith('PROMO_')) {
        const reason = code.slice('PROMO_'.length) as PromoCodeReason;
        setPromoError(t(`offer.reason.${reason}`));
      } else {
        setBuyError((err as Error).message);
      }
    }
  };

  const statusLabel = onTrial
    ? t('status.TRIALING')
    : renewalStopped
      ? t('status.renewalStopped')
      : t('status.ACTIVE');

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      {/* An unpaid subscription invoice is plan state, not a reminder, so it lives
          here rather than only in the (dismissible) NudgeBanner. Without it a
          teacher who dismissed the banner has no path from this tab to the QR.
          The body now says what happens if it goes unpaid — that outcome used to
          be entirely silent. */}
      {data.pendingInvoice && (
        <div className="rounded-[12px] border border-attention-border bg-attention-tint p-[16px]">
          <p className="text-sm font-semibold text-attention-foreground">
            {t('pendingCallout.title', { amount: fmtVnd(data.pendingInvoice.amountVnd, i18n.language) })}
          </p>
          <p className="mt-[6px] text-[13px] leading-[1.6] text-attention-foreground">
            {t(pendingInvoiceHasFreeFallback ? 'pendingCallout.body' : 'pendingCallout.bodyNeutral', {
              date: fmtDate(data.pendingInvoice.dueAt),
              plan: plan.name,
            })}
          </p>
          <div className="mt-[12px] flex flex-wrap items-center gap-[10px]">
            <Button
              className="min-h-[44px]"
              onClick={() => navigate(`/settings/billing/invoices/${data.pendingInvoice!.id}`)}
            >
              {t('pendingCallout.cta')}
            </Button>
            <Button
              variant="link"
              className="min-h-[44px] px-0 text-attention-foreground"
              disabled={cancelInvoice.isPending}
              onClick={() => setConfirming('cancelInvoice')}
            >
              {t('pendingCallout.cancel')}
            </Button>
          </div>
        </div>
      )}

      {/* ONE status card: plan + status + dates + next-invoice date, a divider,
          then the quota meters. The plan card and the usage card used to be two
          separate boxes saying two halves of one thing. */}
      <div className="overflow-hidden rounded-[12px] border border-surface-200 bg-surface-0">
        <div className="flex items-start justify-between gap-4 border-b border-surface-200 p-[16px]">
          <div className="min-w-0">
            <div className="flex items-center gap-[10px]">
              <h2 className="font-display text-[20px] font-semibold tracking-tight text-surface-900">{plan.name}</h2>
              <span
                className={`rounded px-2 py-0.5 text-[12px] font-medium ${
                  renewalStopped ? 'bg-attention-tint text-attention-foreground' : 'bg-brand-50 text-brand-700'
                }`}
              >
                {statusLabel}
              </span>
            </div>

            {onTrial ? (
              <p className="mt-[5px] text-[13px] text-surface-500">
                {t('trialEnds', { date: fmtDate(data.currentPeriodEnd) })}
              </p>
            ) : code === 'FREE' ? (
              <p className="mt-[5px] text-[13px] text-surface-500">{t('freeNoExpiry')}</p>
            ) : renewalStopped ? (
              <>
                <p className="mt-[6px] text-[13px] leading-[1.6] text-surface-600">
                  {t('autoRenewOff', { plan: plan.name, date: fmtDate(data.currentPeriodEnd) })}
                </p>
                {canManageBilling && (
                  <Button
                    variant="link"
                    className="mt-[10px] min-h-[44px] px-0"
                    onClick={() => setConfirming('resume')}
                  >
                    {t('resumeLink')}
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="mt-[5px] text-[13px] text-surface-500">
                  {t('periodEndsCycle', {
                    date: fmtDate(data.currentPeriodEnd),
                    cycle: t(data.billingCycle === 'ANNUAL' ? 'cycleAnnualShort' : 'cycleMonthlyShort'),
                  })}
                </p>
                <p className="mt-[6px] text-[13px] leading-[1.55] text-surface-600">
                  {t('nextInvoice', { date: fmtNextInvoiceDate(data.currentPeriodEnd) })}
                </p>
              </>
            )}
          </div>

          <Button variant="link" className="min-h-[44px] shrink-0 px-0" onClick={() => openUpgrade()}>
            {t('choose.compare')}
          </Button>
        </div>

        {/* Usage — the quota bars' long-term home. The sidebar pair is desktop-only
            (on a phone it lives in a Sheet nothing opens — THI-129 finding), and
            this page IS reachable at 390 through the org menu, so the numbers live
            here too. The sidebar bars stay exactly as they are on desktop. */}
        <div className="flex flex-col gap-[16px] p-[16px]">
          {studentsCapped ? (
            <div>
              <UsageBar
                label={t('usage.studentsBar')}
                used={usage.students}
                limit={plan.maxStudents}
                nearLimit={studentsNearLimit}
              />
              {studentsNearLimit && (
                <p className="mt-[8px] text-[12.5px] leading-[1.55] text-attention-foreground">
                  {t('usage.studentsNearLimit')}
                </p>
              )}
            </div>
          ) : (
            // Unlimited is a fact, not a meter: a bar with no ceiling is a bar
            // that can only ever look empty.
            <div className="flex items-baseline justify-between gap-3 text-[13px]">
              <span className="text-surface-600">{t('usage.studentsBar')}</span>
              <span className="tabular text-surface-700">
                <strong className="font-semibold">{usage.students}</strong> / {t('usage.studentsUnlimitedValue')}
              </span>
            </div>
          )}

          {usage.aiCreditsLimit > 0 && (
            <UsageBar
              label={t('usage.aiCreditsBar')}
              used={usage.aiCreditsUsed}
              limit={usage.aiCreditsLimit}
              exhausted={aiCreditsExhausted}
            />
          )}

          {reconcile && reconcile.quota === 0 && (
            <p className="text-[13px] leading-[1.6] text-surface-500">{t('usage.reconcileOff')}</p>
          )}

          {reconcile && !reconcile.unlimited && reconcile.quota > 0 && (
            <div className="space-y-3">
              <UsageBar
                label={t('usage.reconcileBar')}
                used={reconcile.used}
                limit={reconcile.quota}
                exhausted={reconcileExhausted}
              />
              {reconcileExhausted && (
                <div className="rounded-md border border-attention-border bg-attention-tint p-3">
                  <p className="text-[13px] leading-relaxed text-attention-foreground">{t('usage.reconcileExhausted')}</p>
                  <Button size="sm" className="mt-2 min-h-[36px]" onClick={() => openUpgrade()}>
                    {t('usage.reconcileExhaustedCta')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {data.scheduledChange && (
        <p className="rounded-sm bg-brand-50 px-3 py-2 text-sm text-brand-800">
          {t('scheduledChange', {
            plan: data.scheduledChange.planName,
            date: new Date(data.scheduledChange.startsAt).toLocaleDateString('vi-VN', {
              timeZone: 'Asia/Ho_Chi_Minh',
            }),
          })}
        </p>
      )}

      {/* Plan chooser — vertical rows, because the real settings content column
          is 640px and three cards abreast leave 205px each. 390px is the same
          shape stacked, not a second layout. */}
      {showChooser && (
        <div className="space-y-[12px]">
          <div className="flex flex-col gap-[12px] sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <h3 className="font-display text-[15px] font-semibold leading-[1.4] tracking-tight text-surface-800">
              {onTrial ? t('choose.titleTrial') : t('choose.titleUpgrade')}
            </h3>
            <div className="flex w-full items-center gap-[3px] rounded-[10px] border border-surface-200 bg-surface-0 p-[3px] sm:w-auto sm:shrink-0">
              {(['MONTHLY', 'ANNUAL'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setPickedCycle(c);
                    // Fix wave 2 (minor #2): a stale PROMO_PLAN/CYCLE note
                    // must not linger once the cycle has changed under it.
                    setPromoError(null);
                  }}
                  className={`flex min-h-[44px] flex-1 items-center justify-center gap-[6px] rounded-[8px] px-[12px] text-[12px] font-medium transition-colors sm:min-h-[36px] sm:flex-none ${
                    cycle === c ? 'bg-brand-500 text-white' : 'text-surface-600 hover:text-surface-800'
                  }`}
                >
                  {t(`upgrade.cycle.${c === 'MONTHLY' ? 'monthly' : 'annual'}`)}
                  {c === 'ANNUAL' && (
                    <span className={`font-mono text-[9px] tracking-[0.08em] ${cycle === c ? 'text-white/90' : 'text-brand-600'}`}>
                      −20%
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {offered.map((p) => {
            const annual = cycle === 'ANNUAL';
            // Annual mode leads with the per-month equivalent, like the landing
            // (Johnny, 2026-09-05); the yearly total the QR will encode stays on
            // the line beneath.
            const big = annual ? perMonthFromAnnualVnd(PLAN_PRICES_VND[p].annual) : PLAN_PRICES_VND[p].monthly;
            // THI-200: always through applyPercentOff against the tier's full
            // list price for the picked cycle — never computed inline (§1.5).
            const listVnd = annual ? PLAN_PRICES_VND[p].annual : PLAN_PRICES_VND[p].monthly;
            const offerLineInfo = offerLineFor(p);
            const offerLine = offerLineInfo
              ? t('offer.firstTerm', {
                  amount: fmtVnd(applyPercentOff(listVnd, offerLineInfo.pct).amountVnd, i18n.language),
                  name: offerLineInfo.name,
                })
              : null;
            return (
              <div
                key={p}
                className="flex flex-col gap-[10px] rounded-[12px] border border-surface-200 bg-surface-0 p-[16px] sm:flex-row sm:items-center sm:justify-between sm:gap-5"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-[8px]">
                    <h4 className="font-display text-[16px] font-semibold tracking-tight text-surface-900">
                      {t(`upgrade.tiers.${p.toLowerCase()}`)}
                    </h4>
                    {onTrial && p === code && (
                      <span className="rounded bg-brand-50 px-2 py-0.5 text-[12px] font-medium text-brand-700">
                        {t('planBadge.trial')}
                      </span>
                    )}
                    {/* THI-200 follow-up — same chip as UpgradeDialog's compare
                        table header, next to the plan name. */}
                    {offerLineInfo && (
                      <span className="inline-flex h-[18px] shrink-0 items-center whitespace-nowrap rounded-[6px] bg-brand-50 px-[6px] text-[10px] font-medium text-brand-700">
                        −{offerLineInfo.pct}% kỳ đầu
                      </span>
                    )}
                  </div>
                  <p className="mt-[6px] text-[12.5px] leading-[1.55] text-surface-600">{t(`choose.summary.${p}`)}</p>
                </div>
                <div className="shrink-0 sm:w-[168px] sm:text-right">
                  <div className="tabular text-[19px] font-semibold tracking-tight text-surface-900">
                    {fmtVnd(big, i18n.language)}{annual ? t('choose.perMonthSuffix') : ''}
                  </div>
                  <div className="mt-[1px] text-[11px] text-surface-500">
                    {annual
                      ? t('choose.annualSub', {
                          save: fmtVnd(PLAN_PRICES_VND[p].monthly * 12 - PLAN_PRICES_VND[p].annual, i18n.language),
                          total: fmtVnd(PLAN_PRICES_VND[p].annual, i18n.language),
                        })
                      : t('choose.monthlySub')}
                  </div>
                  {offerLine && (
                    <div className="mt-[3px] text-[11px] font-semibold text-brand-600">{offerLine}</div>
                  )}
                  <Button
                    className="mt-[9px] min-h-[44px] w-full"
                    disabled={upgrade.isPending}
                    onClick={() => handleUpgrade(p)}
                  >
                    {onTrial || code === 'FREE'
                      ? t('choose.ctaChoose', { plan: t(`upgrade.tiers.${p.toLowerCase()}`) })
                      : t('choose.ctaUpgrade', { plan: t(`upgrade.tiers.${p.toLowerCase()}`) })}
                  </Button>
                </div>
              </div>
            );
          })}

          {contextPlan && (
            <div className="flex flex-col gap-[6px]">
              <PromoCodeRow
                planCode={contextPlan}
                billingCycle={cycle}
                value={activePromo ? { code: activePromo.code, check: activePromo.check } : null}
                onAccepted={(codeValue, checkResult) => {
                  setPromo({ code: codeValue, check: checkResult });
                  setPromoError(null);
                }}
                onCleared={() => {
                  setPromo(null);
                  setPromoError(null);
                }}
              />
              {promoError && <p className="text-[12.5px] text-red-700">{promoError}</p>}
            </div>
          )}
        </div>
      )}

      {!onTrial && code === 'CENTER' && (
        <p className="text-[13px] text-surface-500">{t('highestPlan')}</p>
      )}

      {buyError && (
        <p className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">{buyError}</p>
      )}

      {/* Reassurance. Every line is verified against the billing code, and the
          first one is a real selling point: no SaaS holding a card can say it. */}
      <div className="flex flex-col gap-[6px] border-t border-surface-200 pt-[14px]">
        {onPaidTerm ? (
          <p className="text-[12.5px] leading-[1.6] text-surface-500">{t('reassure.payEarly')}</p>
        ) : (
          <p className="text-[12.5px] leading-[1.6] text-surface-500">{t('reassure.noAutoCharge')}</p>
        )}
        {prorates && !data.scheduledChange && (
          <p className="text-[12.5px] leading-[1.6] text-surface-500">{t('prorationHint')}</p>
        )}
        <p className="text-[12.5px] leading-[1.6] text-surface-500">{t('reassure.quotaSoft')}</p>
        {onPaidTerm && data.autoRenew && canManageBilling && (
          <Button
            variant="link"
            className="mt-[2px] min-h-[44px] self-start px-0 text-surface-500"
            onClick={() => setConfirming('cancel')}
          >
            {t('stopRenewal')}
          </Button>
        )}
      </div>

      <AlertDialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === 'resume'
                ? t('resumeConfirm.title')
                : confirming === 'cancelInvoice'
                  ? t('pendingCallout.cancelConfirmTitle')
                  : t('cancelConfirm.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'resume'
                ? t('resumeConfirm.body', { plan: plan.name, date: fmtDate(data.currentPeriodEnd) })
                : confirming === 'cancelInvoice'
                  ? t(
                      // Cancelling a PENDING invoice is not cancelling the plan —
                      // but for the invoice that is *holding the account up*
                      // (a renewal, or a trial's first purchase) silence about
                      // the Free fallback is a lie of omission. Same gate as the
                      // callout body above, so the two can never disagree.
                      pendingInvoiceHasFreeFallback
                        ? 'pendingCallout.cancelConfirmTerm'
                        : 'pendingCallout.cancelConfirm',
                      { plan: plan.name, date: fmtDate(data.currentPeriodEnd) },
                    )
                  : t('cancelConfirm.body', { plan: plan.name, date: fmtDate(data.currentPeriodEnd) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {confirming === 'cancelInvoice' ? t('pendingCallout.cancelKeep') : t('cancelConfirm.back')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirming === 'resume') resume.mutate();
                else if (confirming === 'cancelInvoice') cancelInvoice.mutate(data.pendingInvoice!.id);
                else cancel.mutate();
                setConfirming(null);
              }}
            >
              {confirming === 'resume'
                ? t('resume')
                : confirming === 'cancelInvoice'
                  ? t('pendingCallout.cancel')
                  : t('cancelConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * One labelled quota meter. Heights are pinned in px on purpose: `--spacing` is
 * 0.2rem app-wide, so `h-2` renders 6.4px and the bar would read as a hairline
 * on a phone — which is the width this block exists to serve.
 *
 * Two attention inputs, and the difference between them is a rule, not a
 * preference. `exhausted` is the shipped one: the meter is full, fired at
 * exactly 100%. `nearLimit` fires one step early and is for ceilings that
 * BLOCK an action when crossed — today only Free's student cap. Soft ceilings
 * (AI credits, auto-reconcile) must never use it: they stop automating, manual
 * work continues, and nothing is wrong at 90%.
 */
function UsageBar({
  label,
  used,
  limit,
  exhausted = false,
  nearLimit = false,
}: {
  label: string;
  used: number;
  limit: number;
  exhausted?: boolean;
  nearLimit?: boolean;
}) {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const attention = exhausted || nearLimit;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 text-[13px]">
        <span className={attention ? 'font-medium text-attention-foreground' : 'text-surface-600'}>{label}</span>
        <span className="tabular text-surface-500">
          {used}/{limit}
        </span>
      </div>
      <div className="mt-[6px] h-[8px] overflow-hidden rounded-full bg-surface-100">
        <div
          className={`h-full rounded-full ${attention ? 'bg-attention' : 'bg-brand-500'}`}
          style={{ width: `${exhausted ? 100 : pct}%` }}
        />
      </div>
    </div>
  );
}
